import type { StoreShareCardView } from "@/lib/api/types";

/**
 * The facts a COLD BUYER needs to be true — the ones that decide whether a
 * stranger who taps a shared link can actually buy something.
 *
 * This lives in `shared/lib` rather than in either surface that asks the
 * question, because two surfaces now ask it: the dashboard setup checklist
 * (`useDashboardSetupGuide`) and the share sheet's reminder
 * (`useShareReadiness`). A predicate copied into both is a predicate that
 * eventually disagrees with itself — and the two places where it matters most
 * are "can this store take money" and "did the seller choose their link card",
 * both of which had a hand-written copy in each consumer before this file.
 */

/**
 * Whether the store has a way to get paid at all. COD counts — a cash-on-
 * delivery store is a fully working store, and telling its owner they have no
 * way to take money is simply wrong.
 *
 * Moved here from `useDashboardSetupGuide.ts`, which re-exports it so its own
 * test keeps its import path: the unchanged test IS the evidence that the move
 * changed no behaviour.
 */
export function resolveHasPaymentWay(signals: {
  hasActivePaymentOptions?: boolean | null;
  acceptsCod?: boolean | null;
}): boolean {
  return signals.hasActivePaymentOptions === true || signals.acceptsCod === true;
}

/**
 * Any authored field means the seller owns the card; all-null means the
 * storefront DERIVES one (homepage hero → logo → store name).
 *
 * The distinction is the whole point, and the reason this is not a "does the
 * card exist" check: a derived card is not missing and not broken. Copy built
 * on this predicate must never say "missing" for a false return — see
 * `docs/seller-ux-spec-v1.md` §3.1.
 *
 * Was defined three times over — privately in `PublishSheet.tsx`, privately in
 * the settings `branding-panel.tsx`, and about to be a fourth in the share
 * sheet. All of them now call this.
 */
export function hasAuthoredShareCard(
  card: StoreShareCardView | null | undefined,
): boolean {
  if (!card) return false;
  return Boolean(
    card.imageObjectKey ||
      card.titleAr ||
      card.titleEn ||
      card.descriptionAr ||
      card.descriptionEn,
  );
}

/**
 * Row ids in display order. Pinned by a test: a sixth check cannot reach a
 * share sheet without someone deciding it belongs at the moment a merchant is
 * about to hand their link to a stranger. This is a FLOOR, not a completeness
 * meter — the dashboard guide is the meter.
 */
export const SHARE_READINESS_IDS = [
  "products",
  "payment",
  "contact",
  "shareCard",
  "policy",
] as const;

export type ShareReadinessId = (typeof SHARE_READINESS_IDS)[number];

/**
 * Everything the five checks are decided from. Like `SetupSignals`, all of it
 * is a STORE fact — nothing here is a browser preference or a UI toggle, so a
 * merchant cannot be told their store is unready because of something only
 * their own browser knows.
 */
export type ShareReadinessSignals = {
  hasProducts: boolean;
  hasActivePaymentOptions?: boolean | null;
  acceptsCod?: boolean | null;
  whatsappNumber?: string | null;
  shareCard?: StoreShareCardView | null;
  /** How many store policies are PUBLISHED (`isEnabled`), of the three types. */
  enabledPolicyCount: number;
};

/** Which checks pass, from store facts alone. Pure, so the rule is testable. */
export function resolveShareReadiness(
  signals: ShareReadinessSignals,
): Record<ShareReadinessId, boolean> {
  return {
    products: signals.hasProducts,
    payment: resolveHasPaymentWay(signals),
    contact: Boolean(signals.whatsappNumber?.trim()),
    shareCard: hasAuthoredShareCard(signals.shareCard),
    // ONE published policy, where Store settings counts the section done only
    // at all three. Deliberate: settings is a completeness meter, this is the
    // floor a stranger needs — one published policy already beats none for a
    // buyer deciding whether to trust an unknown shop. Recorded in
    // docs/storefront-analytics.md so neither threshold gets "fixed" to match
    // the other by accident.
    policy: signals.enabledPolicyCount > 0,
  };
}
